<template>
  <div class="homepage" v-if="loaded">
    <!-- Hero (overlaps under 68px sticky header via margin-block-start: -68px in hero component) -->
    <hero :promoted="promoted"></hero>

    <!-- Rails stack, pulled up by -28px to create hero overlap effect -->
    <div class="homepage__rails">
      <section
        v-for="cat in categories"
        :key="cat.id"
        class="rail"
        :class="{ 'rail--top10': cat.id === 195 }"
        :aria-label="cat.name"
      >
        <!-- Rail header row -->
        <div class="rail__header">
          <div class="rail__header-start">
            <h2 class="rail__title">{{ cat.name }}</h2>
            <!-- TOP 10 badge for category 195 -->
            <ui-badge v-if="cat.id === 195" variant="accentSoft" class="rail__badge">TOP 10</ui-badge>
          </div>
          <router-link :to="'category/' + cat.id" class="rail__explore" aria-label="'כל הפריטים ב' + cat.name">
            כל הפריטים
            <svg
              class="rail__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </router-link>
        </div>

        <!-- Splide carousel (vanilla, mounted in updated hook) -->
        <section
          ref="splides"
          :data-cat-id="cat.id"
          :class="['splide', 'rail__splide', { 'rail__splide--top10': cat.id === 195 }]"
        >
          <div class="splide__track">
            <ul class="splide__list">
              <template v-if="cat.id === 195">
                <li
                  v-for="(item, idx) in cat.items.slice(0, 10)"
                  :key="item.id"
                  class="splide__slide"
                >
                  <div class="rail__top10-item">
                    <span class="rail__top10-num" aria-hidden="true">{{ idx + 1 }}</span>
                    <item-tpl :item="item" :lazy="false" @toggle-favorite="onToggleFav" />
                  </div>
                </li>
              </template>
              <template v-else>
                <li
                  v-for="item in cat.items"
                  :key="item.id"
                  class="splide__slide"
                >
                  <item-tpl :item="item" :lazy="false" @toggle-favorite="onToggleFav" />
                </li>
              </template>
            </ul>
          </div>
        </section>
      </section>
    </div>
  </div>
</template>

<script>
import itemTpl from './item_tpl.vue';
import hero from './hero_items.vue';
import UiBadge from './ui/Badge.vue';
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';

const SPLIDE_OPTS = {
  type: 'loop',
  direction: 'rtl',
  perPage: 6,
  perMove: 6,
  gap: '6px',
  pagination: false,
  arrows: true,
  drag: true,
  speed: 600,
  breakpoints: {
    1280: { perPage: 5, perMove: 5 },
    1024: { perPage: 4, perMove: 4 },
    768: { perPage: 3, perMove: 3 },
    640: { perPage: 2, perMove: 2 },
  },
};

const SPLIDE_OPTS_TOP10 = {
  type: 'loop',
  direction: 'rtl',
  perPage: 5,
  perMove: 5,
  gap: '0px',
  pagination: false,
  arrows: true,
  drag: true,
  speed: 600,
  breakpoints: {
    1280: { perPage: 4, perMove: 4 },
    1024: { perPage: 3, perMove: 3 },
    768: { perPage: 2, perMove: 2 },
    640: { perPage: 1, perMove: 1 },
  },
};

export default {
  components: { itemTpl, hero, UiBadge },

  data() {
    return {
      promoted: [],
      categories: [],
      loaded: false,
      splidesList: [],
    };
  },

  methods: {
    onToggleFav(id) {
      this.$emit('toggle-favorite', id);
    },

    initSplides() {
      this.destroySplides();
      const els = this.$refs.splides;
      if (!els) return;
      const list = Array.isArray(els) ? els : [els];
      list.forEach(el => {
        if (!el || el.__splide_mounted) return;
        const isTop10 = el.dataset.catId === '195';
        const opts = isTop10 ? SPLIDE_OPTS_TOP10 : SPLIDE_OPTS;
        // Splide's 'loop' type clones slides to fill the viewport — with a single
        // real slide this clones/renders that same item more than once. Fall back
        // to 'slide' when there's nothing to actually loop.
        const slideCount = el.querySelectorAll('.splide__slide').length;
        const finalOpts = slideCount <= 1 ? Object.assign({}, opts, { type: 'slide', drag: false, arrows: false }) : opts;
        try {
          const sp = new Splide(el, finalOpts).mount();
          el.__splide_mounted = true;
          this.splidesList.push(sp);
        } catch (e) {
          // mount failed — skip this element
        }
      });
    },

    destroySplides() {
      (this.splidesList || []).forEach(sp => {
        try { sp.destroy(true); } catch (e) { /* noop */ }
      });
      this.splidesList = [];
    },

    parse_homepage_data(data, save_to_store) {
      // promoted
      for (var i in data.promoted) {
        data.promoted[i].is_active = i == 0;
      }
      this.promoted = data.promoted;

      // categories — reset loadVideo flag, init hover state
      for (let i_cat in data.categories) {
        data.categories[i_cat]._hovering = false;
        for (let i_item in data.categories[i_cat].items) {
          data.categories[i_cat].items[i_item].loadVideo = false;
        }
      }
      this.categories = data.categories;

      if (save_to_store === true) {
        for (let i in this.promoted) {
          this.$store.commit('update_item', this.promoted[i]);
        }
      }
    },
  },

  updated() {
    window.dispatchEvent(new Event('resize'));
  },

  beforeDestroy() {
    this.destroySplides();
  },

  mounted() {
    var self = this;

    if (this.$store.state.homepage_data.promoted != undefined) {
      this.parse_homepage_data(this.$store.state.homepage_data);
      self.loaded = true;
      self.$nextTick(() => self.initSplides());
    } else {
      self.$emit('show_loader');
      this.api_call({
        url: 'home',
        data: { include_rel_items: false },
        onComplete: function (data) {
          self.$store.state.homepage_data = data.data.data;
          self.parse_homepage_data(data.data.data, false);
          self.$emit('loaded');
          self.loaded = true;
          self.$nextTick(() => self.initSplides());
        },
      });
    }
  },
};
</script>

<style scoped lang="scss">
/* ── Page wrapper ── */
.homepage {
  background: var(--color-bg);
  min-height: 100vh;
}

/* ── Rails container ── */
.homepage__rails {
  display: flex;
  flex-direction: column;
  gap: 56px;
  padding-block-end: 64px;
}

/* ── Rail ── */
.rail {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Rail header */
.rail__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 0 36px 14px;
  position: relative;
  z-index: 12;
}

.rail__header-start {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.rail__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  text-align: start;
  transition: font-size 200ms ease, text-shadow 200ms ease;
}
.rail:hover .rail__title {
  font-size: 23px;
  text-shadow: 0 0 8px rgba(255,255,255,0.1);
}

.rail__badge {
  /* accentSoft variant already styled via UiBadge */
}

/* Explore-all link — hidden until rail hover */
.rail__explore {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-textMuted);
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  font-family: var(--font-hebrew);
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 200ms ease, transform 200ms ease, color var(--duration-fast) var(--easing-default);
}
[dir="rtl"] .rail__explore {
  transform: translateX(-8px);
}
.rail:hover .rail__explore {
  opacity: 1;
  transform: translateX(0);
}
.rail__explore:hover {
  color: var(--color-text);
}
.rail__explore:focus-visible {
  opacity: 1;
  transform: none;
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Chevron — flip in RTL (directional icon) */
.rail__chevron {
  flex-shrink: 0;
  transition: transform var(--duration-fast) var(--easing-default);
}
[dir="rtl"] .rail__chevron {
  transform: scaleX(-1);
}

/* ── Splide rail ── */
.rail__splide {
  display: flex;
  flex-direction: column;
  padding: 0 var(--rail-padding-inline, 36px);
}
.rail__splide :deep(.splide__track) {
  overflow: hidden;
}
.rail__splide :deep(.splide__list) {
  display: flex;
  align-items: stretch;
}
.rail__splide :deep(.splide__slide) {
  display: flex;
}
.rail__splide :deep(.poster-card) {
  width: 100%;
}
.rail__splide :deep(.poster-card__inner) {
  width: 100%;
  max-width: none;
  min-width: 0;
}

/* Splide arrows — Netflix-style edge chevrons */
.rail__splide :deep(.splide__arrows) {
  pointer-events: none;
}
.rail__splide :deep(.splide__arrow) {
  pointer-events: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 56px;
  height: auto;
  transform: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  z-index: 20;
  opacity: 0.85;
  background: rgba(0,0,0,0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 180ms ease, background 180ms ease;
}
.rail__splide :deep(.splide__arrow svg) {
  width: 24px;
  height: 24px;
  fill: #fff;
}
/* RTL site: prev (start) on right, next (end) on left */
.rail__splide :deep(.splide__arrow--prev) {
  right: 0;
  left: auto;
  background: linear-gradient(to left, rgba(0,0,0,0.65) 20%, transparent 100%);
}
.rail__splide :deep(.splide__arrow--next) {
  left: 0;
  right: auto;
  background: linear-gradient(to right, rgba(0,0,0,0.65) 20%, transparent 100%);
}
.rail__splide :deep(.splide__arrow:hover) {
  opacity: 1;
}
.rail__splide :deep(.splide__arrow:disabled) {
  opacity: 0;
}
@media (hover: none) {
  .rail__splide :deep(.splide__arrow) {
    display: none;
  }
}

/* ── Top-10 variant ── */
.rail__top10-item {
  display: flex;
  align-items: flex-end;
  width: 100%;
}

.rail__top10-num {
  font-size: 180px;
  font-weight: 900;
  line-height: 0.85;
  color: transparent;
  -webkit-text-stroke: 3px var(--color-textMuted);
  font-family: var(--font-sans);
  letter-spacing: -10px;
  margin-inline-end: -28px;
  position: relative;
  z-index: 0;
  user-select: none;
  flex-shrink: 0;
}

.rail__splide--top10 :deep(.poster-card__inner) {
  width: 140px;
}
.rail__splide--top10 :deep(.ui-card--poster .ui-card__media-wrap) {
  width: 140px;
  height: 210px;
}
.rail__splide--top10 :deep(.poster-card__img) {
  width: 140px;
  height: 210px;
}

/* ── Mobile adjustments ── */
@media (max-width: 640px) {
  .rail__header {
    padding: 0 var(--rail-padding-inline, 16px) 10px;
  }
  .rail__title {
    font-size: 17px;
  }
  .rail:hover .rail__title {
    font-size: 18px;
  }
  .rail {
    margin-block-end: 32px;
  }
  .homepage__rails {
    margin-block-start: 32px;
    gap:10px;
  }
  .rail__splide {
    padding: 0 var(--rail-padding-inline, 16px);
  }
  .rail__top10-num {
    font-size: 120px;
    -webkit-text-stroke: 2px var(--color-textMuted);
    margin-inline-end: -16px;
    letter-spacing: -6px;
  }
  .rail__splide--top10 :deep(.poster-card__inner),
  .rail__splide--top10 :deep(.ui-card--poster .ui-card__media-wrap),
  .rail__splide--top10 :deep(.poster-card__img) {
    width: 110px;
    height: 165px;
  }
  /* Hide explore-all link on mobile (always visible would crowd) */
  .rail__explore {
    display: none;
  }
}
</style>
